import React, { useState, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ClockIcon,
  SparklesIcon,
  ChartBarIcon,
  CubeIcon,
  ShoppingBagIcon,
  UsersIcon,
  DocumentTextIcon,
  BellIcon,
  ChatBubbleBottomCenterTextIcon,
  CreditCardIcon,
  TruckIcon,
  GiftIcon,
  TagIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ChartPieIcon,
  BoltIcon,
  FunnelIcon,
  PresentationChartLineIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon,
  HeartIcon,
  UserGroupIcon,
  CalculatorIcon,
  DocumentDuplicateIcon,
  ArrowTrendingUpIcon,
  LightBulbIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const AdminFeaturesShowcase = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // כל התכונות של המערכת
  const features = [
    // ניהול מוצרים וקטלוג
    {
      id: 1,
      name: 'ניהול מוצרים מתקדם',
      category: 'products',
      description: 'מערכת ניהול מוצרים מקיפה עם תמיכה בתמונות מרובות, SKU, מחירי B2B/B2C, ניהול מלאי חכם ועוד',
      benefits: ['עדכונים בזמן אמת', 'ניהול מלאי אוטומטי', 'תמיכה במחירים דינמיים', 'העלאת תמונות מרובות'],
      location: '/admin/products',
      status: 'ready',
      icon: CubeIcon,
      tags: ['מלאי', 'מחירים', 'תמונות']
    },
    {
      id: 2,
      name: 'מערכת קטגוריות היררכית',
      category: 'products',
      description: 'קטגוריות עם תמיכה בהיררכיה, תמונות, SEO, וניהול שקיפות מלאי',
      benefits: ['ניווט אינטואיטיבי', 'אופטימיזציה למנועי חיפוש', 'ניהול מלאי לפי קטגוריה', 'תצוגה מותאמת לקטגוריה'],
      location: '/admin/categories',
      status: 'ready',
      icon: TagIcon,
      tags: ['SEO', 'ארגון', 'ניווט']
    },
    {
      id: 3,
      name: 'חבילות ומבצעים (Bundle Deals)',
      category: 'products',
      description: 'יצירת חבילות מוצרים עם הנחות מיוחדות ותמיכה בסוגי הנחה שונים',
      benefits: ['הגדלת ערך הזמנה ממוצע', 'עידוד רכישות מרובות', 'ניהול מבצעים בקלות', 'מעקב אחר ביצועים'],
      location: '/admin/bundles',
      status: 'ready',
      icon: GiftIcon,
      tags: ['מבצעים', 'הנחות', 'מכירות']
    },

    // מערכות הזמנות ותשלומים
    {
      id: 4,
      name: 'מערכת הזמנות מתקדמת',
      category: 'orders',
      description: 'ניהול הזמנות מלא עם סטטוסים, מעקב משלוחים, והיסטוריית הזמנות',
      benefits: ['ניהול פשוט של הזמנות', 'מעקב בזמן אמת', 'הודעות אוטומטיות', 'דוחות מפורטים'],
      location: '/admin/orders',
      status: 'ready',
      icon: ShoppingBagIcon,
      tags: ['הזמנות', 'משלוחים', 'סטטוס']
    },
    {
      id: 5,
      name: 'מערכת חשבוניות Green Invoice',
      category: 'orders',
      description: 'אינטגרציה מלאה עם Green Invoice לייצור חשבוניות אוטומטי',
      benefits: ['חשבוניות אוטומטיות', 'עמידה בדרישות מס הכנסה', 'שליחה ללקוחות', 'ניהול מרכזי'],
      location: '/admin/payments',
      status: 'ready',
      icon: DocumentTextIcon,
      tags: ['חשבוניות', 'מיסוי', 'אוטומציה']
    },
    {
      id: 6,
      name: 'חשבונית מרוכזת (Bulk Invoice)',
      category: 'orders',
      description: 'יצירת חשבונית אחת עבור הזמנות מרובות של לקוח',
      benefits: ['חיסכון בזמן', 'נוחות ללקוחות B2B', 'ניהול פיננסי יעיל', 'דוחות מאוחדים'],
      location: '/admin/payments',
      status: 'ready',
      icon: DocumentDuplicateIcon,
      tags: ['B2B', 'חשבוניות', 'יעילות']
    },
    {
      id: 7,
      name: 'מעקב משלוחים בזמן אמת',
      category: 'orders',
      description: 'מערכת מעקב משלוחים עם עדכונים בזמן אמת ללקוחות',
      benefits: ['שקיפות מלאה', 'הפחתת פניות', 'שיפור שביעות רצון', 'עדכונים אוטומטיים'],
      location: '/track',
      status: 'ready',
      icon: TruckIcon,
      tags: ['משלוחים', 'מעקב', 'שקיפות']
    },
    {
      id: 8,
      name: 'Fast Checkout B2B',
      category: 'orders',
      description: 'תהליך הזמנה מהיר למיוחד ללקוחות עסקיים עם הזמנה חוזרת',
      benefits: ['הזמנה מהירה', 'חוויית משתמש משופרת', 'הגדלת המרות', 'נוחות ללקוחות חוזרים'],
      location: '/fast-checkout',
      status: 'ready',
      icon: BoltIcon,
      tags: ['B2B', 'מהירות', 'המרות']
    },
    {
      id: 36,
      name: 'תבניות הזמנה (Order Templates)',
      category: 'orders',
      description: 'שמירת הזמנות חוזרות כתבניות לשימוש מהיר - מושלם ללקוחות B2B',
      benefits: ['הזמנה מהירה של מוצרים קבועים', 'חיסכון בזמן', 'שיתוף תבניות בין משתמשים', 'מעקב שימוש בתבניות'],
      location: '/account/order-templates',
      status: 'ready',
      icon: DocumentDuplicateIcon,
      tags: ['B2B', 'תבניות', 'הזמנות חוזרות']
    },

    // ניהול לקוחות וחברות
    {
      id: 9,
      name: 'ניהול לקוחות CRM',
      category: 'customers',
      description: 'מערכת CRM מלאה עם היסטוריית רכישות, העדפות ומידע מפורט',
      benefits: ['הכרת הלקוחות', 'שיווק ממוקד', 'שירות משופר', 'מעקב אחר ערך לקוח'],
      location: '/admin/customers',
      status: 'ready',
      icon: UsersIcon,
      tags: ['CRM', 'לקוחות', 'מכירות']
    },
    {
      id: 10,
      name: 'ניהול חברות B2B',
      category: 'customers',
      description: 'ניהול חברות עסקיות עם מגבלות אשראי, תנאי תשלום והנחות',
      benefits: ['ניהול אשראי', 'תנאי תשלום גמישים', 'הנחות מותאמות', 'דוחות פיננסיים'],
      location: '/admin/companies',
      status: 'ready',
      icon: BuildingOfficeIcon,
      tags: ['B2B', 'אשראי', 'חברות']
    },
    {
      id: 11,
      name: 'חוזי תמחור (Contract Pricing)',
      category: 'customers',
      description: 'מחירים מיוחדים ותנאים מותאמים אישית לכל חברה',
      benefits: ['מחירים גמישים', 'ניהול הסכמים', 'אוטומציה של הנחות', 'שימור לקוחות'],
      location: '/admin/contracts',
      status: 'ready',
      icon: DocumentTextIcon,
      tags: ['B2B', 'מחירים', 'הסכמים']
    },
    {
      id: 12,
      name: 'מערכת אשראי ומגבלות',
      category: 'customers',
      description: 'ניהול אשראי ללקוחות B2B עם התראות ומעקב',
      benefits: ['בקרת אשראי', 'הפחתת חובות', 'מעקב אוטומטי', 'התראות מוקדמות'],
      location: '/admin/credit-debug',
      status: 'ready',
      icon: CreditCardIcon,
      tags: ['אשראי', 'פיננסים', 'בקרה']
    },

    // אנליטיקה ודוחות
    {
      id: 13,
      name: 'דשבורד BI מתקדם',
      category: 'analytics',
      description: 'דשבורד מקיף עם KPIs, גרפים אינטראקטיביים ותובנות עסקיות',
      benefits: ['תובנות בזמן אמת', 'קבלת החלטות מבוססת נתונים', 'ויזואליזציה מתקדמת', 'התראות חכמות'],
      location: '/admin',
      status: 'ready',
      icon: ChartPieIcon,
      tags: ['BI', 'דוחות', 'תובנות']
    },
    {
      id: 14,
      name: 'ניתוח עסקי מקיף',
      category: 'analytics',
      description: 'דוחות עסקיים מעמיקים עם מגמות, תחזיות והשוואות',
      benefits: ['מעקב אחר מגמות', 'זיהוי הזדמנויות', 'ניתוח תחרותי', 'תחזיות מכירות'],
      location: '/admin/business-analytics',
      status: 'ready',
      icon: ChartBarIcon,
      tags: ['אנליטיקה', 'תחזיות', 'מגמות']
    },
    {
      id: 15,
      name: 'ניתוח רווחיות',
      category: 'analytics',
      description: 'ניתוח מעמיק של רווחיות מוצרים, קטגוריות ולקוחות',
      benefits: ['זיהוי מוצרים רווחיים', 'אופטימיזציה של מחירים', 'הפחתת עלויות', 'שיפור שוליים'],
      location: '/admin/profitability',
      status: 'ready',
      icon: PresentationChartLineIcon,
      tags: ['רווחיות', 'שוליים', 'אופטימיזציה']
    },
    {
      id: 16,
      name: 'אופטימיזציית מלאי',
      category: 'analytics',
      description: 'המלצות חכמות לניהול מלאי אופטימלי',
      benefits: ['הפחתת מלאי עודף', 'מניעת חוסרים', 'חיסכון בעלויות', 'תחזיות ביקוש'],
      location: '/admin/inventory-optimization',
      status: 'ready',
      icon: CubeIcon,
      tags: ['מלאי', 'אופטימיזציה', 'תחזיות']
    },
    {
      id: 17,
      name: 'אנליטיקה בזמן אמת',
      category: 'analytics',
      description: 'מעקב בזמן אמת אחר פעילות באתר, מכירות ומשתמשים',
      benefits: ['נתונים חיים', 'תגובה מהירה', 'מעקב אחר אירועים', 'התראות מיידיות'],
      location: '/admin/real-time-analytics',
      status: 'ready',
      icon: BoltIcon,
      tags: ['זמן אמת', 'מעקב', 'אירועים']
    },
    {
      id: 18,
      name: 'משפכי המרה (Conversion Funnels)',
      category: 'analytics',
      description: 'ניתוח מסע הלקוח וזיהוי נקודות נפילה',
      benefits: ['שיפור המרות', 'זיהוי חסמים', 'אופטימיזציה של תהליכים', 'הגדלת הכנסות'],
      location: '/admin/conversion-funnels',
      status: 'ready',
      icon: FunnelIcon,
      tags: ['המרות', 'אופטימיזציה', 'משתמשים']
    },
    {
      id: 19,
      name: 'אנליטיקה מתקדמת - GA4',
      category: 'analytics',
      description: 'אינטגרציה מלאה עם Google Analytics 4',
      benefits: ['מעקב מתקדם', 'אירועים מותאמים', 'דוחות מפורטים', 'שילוב עם Google'],
      location: '/admin/analytics-management',
      status: 'ready',
      icon: GlobeAltIcon,
      tags: ['Google', 'מעקב', 'אנליטיקה']
    },
    {
      id: 20,
      name: 'ניתוח תזרים מזומנים',
      category: 'analytics',
      description: 'תחזיות תזרים מזומנים ותכנון פיננסי',
      benefits: ['תכנון פיננסי', 'ניהול נזילות', 'זיהוי מגמות', 'ניהול סיכונים'],
      location: '/admin/cash-flow-prediction',
      status: 'ready',
      icon: CurrencyDollarIcon,
      tags: ['פיננסים', 'תחזיות', 'תזרים']
    },
    {
      id: 37,
      name: 'מעקב מסע לקוח (User Journey)',
      category: 'analytics',
      description: 'מעקב מפורט אחר התנהגות משתמשים באתר וזיהוי דפוסים',
      benefits: ['הבנת התנהגות לקוחות', 'שיפור חוויית משתמש', 'זיהוי נקודות חיכוך', 'אופטימיזציה של מסלולים'],
      location: '/admin/analytics-management',
      status: 'ready',
      icon: ArrowTrendingUpIcon,
      tags: ['מסע לקוח', 'התנהגות', 'אנליטיקה']
    },
    {
      id: 38,
      name: 'Facebook Pixel & Analytics',
      category: 'analytics',
      description: 'אינטגרציה מלאה עם Facebook Pixel למעקב פרסומות',
      benefits: ['מעקב המרות פרסומיות', 'אופטימיזציה של קמפיינים', 'Retargeting מתקדם', 'ROI ברור'],
      location: '/admin/analytics-management',
      status: 'ready',
      icon: GlobeAltIcon,
      tags: ['Facebook', 'פרסום', 'Pixel']
    },
    {
      id: 39,
      name: 'ייצוא נתונים (CSV/Excel)',
      category: 'analytics',
      description: 'ייצוא כל הנתונים לקבצי CSV/Excel לניתוח חיצוני',
      benefits: ['ניתוח מתקדם', 'שילוב עם מערכות אחרות', 'גיבוי נתונים', 'דוחות מותאמים אישית'],
      location: '/admin',
      status: 'ready',
      icon: DocumentTextIcon,
      tags: ['ייצוא', 'CSV', 'דוחות']
    },

    // תקשורת ושירות לקוחות
    {
      id: 21,
      name: 'מערכת התראות בזמן אמת',
      category: 'communication',
      description: 'הודעות בזמן אמת עם Socket.IO ללקוחות ומנהלים',
      benefits: ['עדכונים מיידיים', 'שיפור התקשורת', 'הפחתת פניות', 'שקיפות מלאה'],
      location: '/notifications',
      status: 'ready',
      icon: BellIcon,
      tags: ['התראות', 'זמן אמת', 'תקשורת']
    },
    {
      id: 22,
      name: 'מערכת משא ומתן על מחירים',
      category: 'communication',
      description: 'פלטפורמה למשא ומתן על מחירים בין לקוחות B2B למוכרים',
      benefits: ['שקיפות במחירים', 'שיפור קשרי לקוחות', 'גמישות במחירים', 'מעקב אחר הצעות'],
      location: '/admin/price-negotiations',
      status: 'ready',
      icon: ChatBubbleBottomCenterTextIcon,
      tags: ['B2B', 'משא ומתן', 'מחירים']
    },
    {
      id: 23,
      name: 'מערכת ביקורות ודירוגים',
      category: 'communication',
      description: 'ניהול ביקורות לקוחות ודירוגים של מוצרים',
      benefits: ['בניית אמון', 'שיפור מוצרים', 'חיזוק המותג', 'תובנות לקוחות'],
      location: '/admin/reviews',
      status: 'ready',
      icon: StarIcon,
      tags: ['ביקורות', 'דירוגים', 'משוב']
    },

    // חווית משתמש ונגישות
    {
      id: 24,
      name: 'עיצוב Mobile-First',
      category: 'ux',
      description: 'כל המערכת מותאמת למובייל עם חווית משתמש מעולה',
      benefits: ['גישה מכל מכשיר', 'מהירות טעינה', 'חוויה אחידה', 'הגדלת נגישות'],
      location: '/',
      status: 'ready',
      icon: DevicePhoneMobileIcon,
      tags: ['מובייל', 'UX', 'נגישות']
    },
    {
      id: 25,
      name: 'תמיכה מלאה בעברית',
      category: 'ux',
      description: 'ממשק מלא בעברית עם תמיכה ב-RTL',
      benefits: ['נוחות למשתמשים ישראלים', 'קריאות גבוהה', 'מקצועיות', 'הקטנת טעויות'],
      location: '/',
      status: 'ready',
      icon: GlobeAltIcon,
      tags: ['עברית', 'RTL', 'שפה']
    },
    {
      id: 26,
      name: 'רשימת משאלות (Wishlist)',
      category: 'ux',
      description: 'שמירת מוצרים מועדפים ושיתוף רשימות',
      benefits: ['הגדלת מעורבות', 'עידוד חזרה לאתר', 'מכירות עתידיות', 'תובנות העדפות'],
      location: '/wishlist',
      status: 'ready',
      icon: HeartIcon,
      tags: ['משאלות', 'מעורבות', 'שמירה']
    },
    {
      id: 27,
      name: 'חיפוש מתקדם וסינונים',
      category: 'ux',
      description: 'מנוע חיפוש חכם עם סינונים מתקדמים',
      benefits: ['מציאת מוצרים בקלות', 'שיפור חוויה', 'הגדלת המרות', 'חיסכון בזמן'],
      location: '/products',
      status: 'ready',
      icon: MagnifyingGlassIcon,
      tags: ['חיפוש', 'סינון', 'ניווט']
    },
    {
      id: 40,
      name: 'ניהול כתובות מתקדם',
      category: 'ux',
      description: 'ניהול מספר כתובות משלוח וחיוב עבור כל לקוח',
      benefits: ['גמישות למשתמשים', 'כתובות ברירת מחדל', 'כתובות שונות לחברות', 'חיסכון בזמן'],
      location: '/account',
      status: 'ready',
      icon: BuildingOfficeIcon,
      tags: ['כתובות', 'משלוחים', 'ניהול']
    },

    // אבטחה ותשתית
    {
      id: 28,
      name: 'אבטחה מתקדמת',
      category: 'security',
      description: 'הצפנה, JWT, Rate Limiting, Helmet.js והגנות נוספות',
      benefits: ['הגנה על נתונים', 'מניעת התקפות', 'עמידה בתקנים', 'שקט נפשי'],
      location: '/admin/settings',
      status: 'ready',
      icon: ShieldCheckIcon,
      tags: ['אבטחה', 'הצפנה', 'הגנה']
    },
    {
      id: 29,
      name: 'ניהול תפקידים והרשאות',
      category: 'security',
      description: 'מערכת הרשאות מתקדמת - Admin, B2B, B2C',
      benefits: ['שליטה מלאה', 'אבטחת מידע', 'הפרדת סמכויות', 'ניהול צוות'],
      location: '/admin/settings',
      status: 'ready',
      icon: UserGroupIcon,
      tags: ['הרשאות', 'תפקידים', 'אבטחה']
    },
    {
      id: 41,
      name: 'מערכת מבצעים והנחות',
      category: 'marketing',
      description: 'יצירת מבצעים, קופונים והנחות מותאמות אישית',
      benefits: ['עידוד מכירות', 'מבצעים ממוקדים', 'קופוני הנחה', 'תאריכי תפוגה'],
      location: '/admin/products',
      status: 'ready',
      icon: TagIcon,
      tags: ['מבצעים', 'הנחות', 'קופונים']
    },

    // תכונות עתידיות
    {
      id: 30,
      name: 'מערכת המלצות AI',
      category: 'ai',
      description: 'המלצות מוצרים מבוססות AI והתנהגות משתמשים',
      benefits: ['הגדלת מכירות', 'חוויה מותאמת אישית', 'Cross-selling', 'Up-selling'],
      location: '/products',
      status: 'ready',
      icon: LightBulbIcon,
      tags: ['AI', 'המלצות', 'מכירות']
    },
    {
      id: 42,
      name: 'הזמנות חוזרות אוטומטיות',
      category: 'orders',
      description: 'הזמנות אוטומטיות על בסיס לו"ז (יומי/שבועי/חודשי)',
      benefits: ['אוטומציה מלאה', 'אף פעם לא נגמר המלאי', 'חיסכון בזמן', 'תזרים צפוי'],
      location: '/account/order-templates',
      status: 'coming-soon',
      icon: ClockIcon,
      tags: ['אוטומציה', 'הזמנות', 'B2B']
    },
    {
      id: 43,
      name: 'מערכת אישורים (Approval Workflow)',
      category: 'b2b',
      description: 'תהליך אישור הזמנות לפי סכומים ותפקידים',
      benefits: ['בקרה פיננסית', 'מניעת חריגות', 'מעקב אחר אישורים', 'היררכיה ארגונית'],
      location: '/admin/orders',
      status: 'coming-soon',
      icon: CheckCircleIcon,
      tags: ['B2B', 'אישורים', 'בקרה']
    },
    {
      id: 44,
      name: 'בקשות הצעת מחיר (RFQ)',
      category: 'b2b',
      description: 'מערכת לבקשת הצעות מחיר מותאמות אישית',
      benefits: ['משא ומתן גמיש', 'הצעות מחיר מותאמות', 'ניהול תהליך מכירה', 'שקיפות'],
      location: '/price-negotiations',
      status: 'coming-soon',
      icon: ChatBubbleBottomCenterTextIcon,
      tags: ['RFQ', 'הצעות מחיר', 'B2B']
    },
    {
      id: 45,
      name: 'ניהול תקציבים למחלקות',
      category: 'b2b',
      description: 'הגדרת תקציבים למחלקות עם התראות וחסימות',
      benefits: ['שליטה בהוצאות', 'מניעת חריגות תקציב', 'דוחות למחלקה', 'התראות מוקדמות'],
      location: '/admin/companies',
      status: 'coming-soon',
      icon: CalculatorIcon,
      tags: ['תקציבים', 'B2B', 'בקרה']
    },
    {
      id: 31,
      name: 'צ\'אטבוט אינטליגנטי',
      category: 'ai',
      description: 'עוזר וירטואלי חכם לשירות לקוחות 24/7',
      benefits: ['זמינות מלאה', 'מענה מהיר', 'הפחתת עומס', 'שביעות רצון'],
      location: '/',
      status: 'coming-soon',
      icon: ChatBubbleBottomCenterTextIcon,
      tags: ['AI', 'שירות', 'צ\'אט']
    },
    {
      id: 32,
      name: 'ניתוח סנטימנט',
      category: 'ai',
      description: 'ניתוח אוטומטי של ביקורות ומשובי לקוחות',
      benefits: ['הבנת לקוחות', 'זיהוי בעיות', 'שיפור מוצרים', 'מעקב מוניטין'],
      location: '/admin/reviews',
      status: 'coming-soon',
      icon: ChartBarIcon,
      tags: ['AI', 'סנטימנט', 'משוב']
    },
    {
      id: 33,
      name: 'אפליקציה ניידת',
      category: 'mobile',
      description: 'אפליקציות native ל-iOS ו-Android',
      benefits: ['חוויה טובה יותר', 'התראות Push', 'ביצועים גבוהים', 'נוכחות מותגית'],
      location: '/',
      status: 'coming-soon',
      icon: DevicePhoneMobileIcon,
      tags: ['אפליקציה', 'מובייל', 'Native']
    },
    {
      id: 34,
      name: 'תוכנית נאמנות',
      category: 'marketing',
      description: 'מערכת נקודות, הטבות ותגמולים ללקוחות נאמנים',
      benefits: ['שימור לקוחות', 'הגדלת רכישות חוזרות', 'מעורבות גבוהה', 'בידול תחרותי'],
      location: '/account',
      status: 'coming-soon',
      icon: GiftIcon,
      tags: ['נאמנות', 'תגמולים', 'שימור']
    },
    {
      id: 35,
      name: 'שילוב עם ERP',
      category: 'integration',
      description: 'אינטגרציה עם מערכות ERP מובילות',
      benefits: ['אוטומציה מלאה', 'סנכרון נתונים', 'הפחתת טעויות', 'יעילות תפעולית'],
      location: '/admin/settings',
      status: 'coming-soon',
      icon: BuildingOfficeIcon,
      tags: ['ERP', 'אינטגרציה', 'אוטומציה']
    },
  ];

  // קטגוריות
  const categories = [
    { value: 'all', label: 'כל הקטגוריות', icon: ChartPieIcon },
    { value: 'products', label: 'מוצרים וקטלוג', icon: CubeIcon },
    { value: 'orders', label: 'הזמנות ותשלומים', icon: ShoppingBagIcon },
    { value: 'customers', label: 'לקוחות וחברות', icon: UsersIcon },
    { value: 'analytics', label: 'אנליטיקה ודוחות', icon: ChartBarIcon },
    { value: 'communication', label: 'תקשורת ושירות', icon: BellIcon },
    { value: 'ux', label: 'חווית משתמש', icon: DevicePhoneMobileIcon },
    { value: 'security', label: 'אבטחה', icon: ShieldCheckIcon },
    { value: 'marketing', label: 'שיווק', icon: ArrowTrendingUpIcon },
    { value: 'b2b', label: 'תכונות B2B מתקדמות', icon: BuildingOfficeIcon },
    { value: 'ai', label: 'בינוי מלאכותית', icon: LightBulbIcon },
    { value: 'mobile', label: 'מובייל', icon: DevicePhoneMobileIcon },
    { value: 'integration', label: 'אינטגרציות', icon: BuildingOfficeIcon },
  ];

  // סטטוסים
  const statuses = [
    { value: 'all', label: 'כל הסטטוסים', color: 'gray' },
    { value: 'ready', label: 'מוכן', color: 'green' },
    { value: 'in-progress', label: 'בפיתוח', color: 'yellow' },
    { value: 'coming-soon', label: 'בקרוב', color: 'blue' },
  ];

  // סינון תכונות
  const filteredFeatures = useMemo(() => {
    return features.filter(feature => {
      const matchesSearch = feature.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           feature.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           feature.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || feature.category === selectedCategory;
      const matchesStatus = selectedStatus === 'all' || feature.status === selectedStatus;
      
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [searchQuery, selectedCategory, selectedStatus, features]);

  // סטטיסטיקות
  const stats = useMemo(() => {
    return {
      total: features.length,
      ready: features.filter(f => f.status === 'ready').length,
      inProgress: features.filter(f => f.status === 'in-progress').length,
      comingSoon: features.filter(f => f.status === 'coming-soon').length,
    };
  }, [features]);

  // פונקציה לקבלת צבע סטטוס
  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'bg-green-100 text-green-800 border-green-200';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'coming-soon': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready': return <CheckCircleIcon className="w-4 h-4" />;
      case 'in-progress': return <ClockIcon className="w-4 h-4" />;
      case 'coming-soon': return <SparklesIcon className="w-4 h-4" />;
      default: return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ready': return 'מוכן';
      case 'in-progress': return 'בפיתוח';
      case 'coming-soon': return 'בקרוב';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      {/* כותרת */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-2xl p-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            <SparklesIcon className="w-12 h-12" />
            <div>
              <h1 className="text-4xl font-bold mb-2">תכונות המערכת</h1>
              <p className="text-blue-100 text-lg">
                מערכת מסחר B2B/B2C מתקדמת עם {stats.total} תכונות חזקות 🚀
              </p>
            </div>
          </div>

          {/* סטטיסטיקות */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-bold mb-1">{stats.total}</div>
              <div className="text-sm text-blue-100">סה"כ תכונות</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-bold mb-1 text-green-300">{stats.ready}</div>
              <div className="text-sm text-blue-100">מוכנות</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-bold mb-1 text-yellow-300">{stats.inProgress}</div>
              <div className="text-sm text-blue-100">בפיתוח</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-bold mb-1 text-blue-300">{stats.comingSoon}</div>
              <div className="text-sm text-blue-100">בקרוב</div>
            </div>
          </div>
        </div>
      </div>

      {/* סינונים */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* חיפוש */}
          <div className="mb-6">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="חפש תכונה..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* קטגוריות */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">קטגוריה</label>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                    selectedCategory === cat.value
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <cat.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* סטטוס */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">סטטוס</label>
            <div className="flex flex-wrap gap-2">
              {statuses.map((status) => (
                <button
                  key={status.value}
                  onClick={() => setSelectedStatus(status.value)}
                  className={`px-4 py-2 rounded-lg border transition-all ${
                    selectedStatus === status.value
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <span className="text-sm font-medium">{status.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* רשימת תכונות */}
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 text-gray-600">
          נמצאו {filteredFeatures.length} תכונות
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredFeatures.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-blue-400 overflow-hidden group"
              >
                {/* Header */}
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 border-b border-gray-100">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-3 bg-white rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full border ${getStatusColor(feature.status)}`}>
                      {getStatusIcon(feature.status)}
                      <span className="text-xs font-semibold">
                        {getStatusLabel(feature.status)}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {feature.name}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                {/* Benefits */}
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    למה זה טוב?
                  </h4>
                  <ul className="space-y-2 mb-4">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {feature.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Location */}
                  {feature.status === 'ready' && (
                    <a
                      href={feature.location}
                      className="block w-full text-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg font-medium"
                    >
                      עבור לתכונה ←
                    </a>
                  )}
                  {feature.status !== 'ready' && (
                    <div className="block w-full text-center px-4 py-2 bg-gray-100 text-gray-500 rounded-lg font-medium">
                      {feature.status === 'in-progress' ? 'בפיתוח...' : 'בקרוב'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredFeatures.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <MagnifyingGlassIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">לא נמצאו תכונות</h3>
            <p className="text-gray-600">נסה לשנות את החיפוש או הסינונים</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeaturesShowcase;

